import { AppFeatureWidget } from '@orcha/micro-library-widgets-ui';

import AppCard from 'src/components/AppCard';
import { AppCardProps } from 'src/components/AppCard/AppCard';
import DownloadDialog from 'src/components/DownloadDialog';
import IAppCardAppData from '@orcha/types/app-card-app-data';
import { stringSlugifier } from '@orcha/utils';
import { useRouter } from 'next/router';
import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';

export type AppFeatureProps = {
  app?: IAppCardAppData;
  appCardProps?: Omit<AppCardProps, 'appData'>;
  showAdditionalInfo?: boolean;
};

const AppFeature = (props: AppFeatureProps) => {
  const { app, appCardProps, showAdditionalInfo } = props;

  const router = useRouter();
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const { t } = useTranslation('microsite');

  if (!app) {
    return <></>;
  }

  const { appId, name, logoUrl, cost, platformData, appStatus } = app;

  const transformedPlatformData = app.platformData
    ?.filter((p) => p.platformName)
    .map((p) => {
      const { platformName, level, score, levelFurtherInfoUrl } = p;
      return {
        platformName,
        platformTitle: platformName
          ? t(`appCommon:platformLabels.${platformName}` as const)
          : '',
        score: score ? Math.round(score) : undefined,
        ...(level && {
          level: t('appCard:level.label', {
            level,
          }),
        }),
        ...(levelFurtherInfoUrl && {
          levelFurtherInfo: t('appCard:level.explanation', {
            linkTarget: levelFurtherInfoUrl,
          }),
        }),
      };
    });

  const appNameSlugified = app && stringSlugifier(app.name);
  const appDetailsUrl = `/app/${appNameSlugified}/${app?.appId.toLowerCase()}`;

  const handleDownloadClick = () => {
    setIsDownloadDialogOpen(true);
  };

  const handleCardClick = () => {
    router.push(appDetailsUrl);
  };

  return (
    <>
      <AppFeatureWidget
        FlexibleImageComponent={Image}
        showAdditionalInfo={showAdditionalInfo ?? false}
        transformedPlatformData={transformedPlatformData}
        handleDownloadClick={handleDownloadClick}
        handleViewAppDetailsClick={handleCardClick}
        appCardComponent={<AppCard appData={app} {...appCardProps} />}
        translations={{
          appDetailsButton: t('appFeatureWidget.appDetailsButton'),
          downloadButton: t('appFeatureWidget.downloadButton'),
          platformAccreditationsHeading: t('appDetails:accreditationsHeading'),
          accreditationBadgeTooltipText: t('appDetails:accreditationAltText'),
        }}
        {...props}
      />
      {isDownloadDialogOpen && (
        <DownloadDialog
          app={{
            appId,
            name,
            logoUrl,
            cost,
            platforms: platformData
              .filter((x) => x.platformName)
              .map((p) => {
                return {
                  platformName: p.platformName,
                  downloadLink: p.downloadLink,
                };
              }),
            appStatus,
          }}
          open={isDownloadDialogOpen}
          handleClose={() => setIsDownloadDialogOpen(false)}
        />
      )}
    </>
  );
};

export default AppFeature;
